import { captureBrowserException } from "@workspace/analytics/browser";
import { Button } from "@workspace/ui/components/button";
import Link from "next/link";
import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { useStableSession } from "@/lib/auth/use-stable-session";

type ErrorFallbackProps = {
  resetErrorBoundary: () => void;
};

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureBrowserException(error, errorInfo);
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback resetErrorBoundary={this.resetErrorBoundary} />;
    }

    return this.props.children;
  }
}

function SupportLink() {
  const { status } = useStableSession();
  const isAuthed = status === "authenticated";

  return (
    <Link href={isAuthed ? "/support" : "mailto:support@vacwork.app"}>
      <Button>Contact us</Button>
    </Link>
  );
}

function ErrorFallback({ resetErrorBoundary }: ErrorFallbackProps) {
  return (
    <div className="h-[calc(100vh-200px)] w-full">
      <div className="mx-auto mt-8 flex h-full max-w-3xl flex-col items-center justify-center">
        <div className="mt-8 mb-8 flex flex-col items-center justify-between text-center">
          <h2 className="mb-4 font-medium">Something went wrong</h2>
          <p className="text-muted-foreground text-sm">
            An unexpected error has occurred. We are sorry for the
            inconvenience. <br /> Please try again or contact support if the
            problem persists.
          </p>
        </div>

        <div className="flex space-x-4">
          <Button onClick={resetErrorBoundary} variant="outline">
            Try again
          </Button>

          <SupportLink />
        </div>
      </div>
    </div>
  );
}

export { ErrorBoundary };
